// Plain-language "what your result means" + a key limitation, per calculator slug.
// Rendered by CalculatorLayout below the result to help numbers convert to consultations.

export const CALCULATOR_GUIDANCE = {
  'bmi': {
    whatItMeans: 'Your BMI places your weight into a general category (underweight, healthy, overweight or obese) relative to your height.',
    limitation: 'BMI cannot tell muscle from fat or account for age, ethnicity or where fat is stored, so athletes and older adults are often mis-classified.',
  },
  'bmi-prime': {
    whatItMeans: 'BMI Prime shows how far your BMI sits from the upper healthy limit (25). A value under 1.0 is within the healthy range.',
    limitation: 'Like BMI, it ignores body composition and fat distribution.',
  },
  'bsa': {
    whatItMeans: 'Body Surface Area estimates your total skin area, used clinically for dosing and physiological scaling.',
    limitation: 'It is a mathematical estimate, not a measure of health, fitness or body fat.',
  },
  'body-fat': {
    whatItMeans: 'This estimates the share of your weight that is fat, a clearer picture of body composition than weight alone.',
    limitation: 'Tape and formula estimates carry a margin of error; DEXA or calipers are more precise.',
  },
  'lean-body-mass': {
    whatItMeans: 'Lean body mass is everything except fat (muscle, bone, organs and water), useful for tracking muscle and planning protein.',
    limitation: 'It is derived from body-fat estimates, so it inherits the same margin of error.',
  },
  'ideal-weight': {
    whatItMeans: 'This gives a healthy target weight range based on established medical formulas and your height.',
    limitation: 'It is a population estimate; your ideal weight also depends on muscle, frame and health history.',
  },
  'ffmi': {
    whatItMeans: 'FFMI reflects how much muscle you carry relative to height, a better physique marker than BMI for trained people.',
    limitation: 'It relies on body-fat estimates and means less for beginners or those with high body fat.',
  },
  'waist-to-hip': {
    whatItMeans: 'Your waist-to-hip ratio shows how fat is distributed and flags higher cardiometabolic risk when elevated.',
    limitation: 'It is a screening indicator only and does not diagnose any condition.',
  },
  'heart-rate-zones': {
    whatItMeans: 'Your zones show target heart-rate ranges for warm-up, fat-burn, cardio and peak effort so you can train with purpose.',
    limitation: 'Zones use age-based formulas; medication and fitness level can shift your true zones.',
  },
  'vo2-max': {
    whatItMeans: 'VO₂ max estimates your aerobic fitness, how efficiently your body uses oxygen during exercise.',
    limitation: 'Field estimates are approximate; lab testing is the gold standard.',
  },
  'one-rep-max': {
    whatItMeans: 'Your estimated 1RM is the heaviest weight you could lift once, useful for planning training loads.',
    limitation: 'It is a prediction from sub-maximal lifts; never attempt a true max without supervision.',
  },
  'pace': {
    whatItMeans: 'This shows your running pace, speed and projected finish times for common distances.',
    limitation: 'Real-world times vary with terrain, weather, fatigue and training.',
  },
  'pregnancy-weight-gain': {
    whatItMeans: 'This gives a healthy weight-gain range for your pregnancy, based on your pre-pregnancy BMI and trimester.',
    limitation: 'Every pregnancy is different; always follow your obstetrician\u2019s individual guidance.',
  },
  'sleep': {
    whatItMeans: 'This suggests bed or wake times aligned with 90-minute sleep cycles so you wake feeling rested.',
    limitation: 'Ideal sleep need varies by person; consistency matters more than exact cycle timing.',
  },
  'intermittent-fasting': {
    whatItMeans: 'This maps your fasting and eating windows for the method you choose.',
    limitation: 'Fasting is not suitable for everyone, including pregnancy, diabetes on medication or a history of disordered eating.',
  },
  'calorie-burn': {
    whatItMeans: 'This estimates the energy used during an activity from your weight, duration and intensity.',
    limitation: 'Estimates use average values; individual burn varies with fitness and effort.',
  },
  'steps-to-calories': {
    whatItMeans: 'This converts your daily steps into approximate calories burned and distance walked.',
    limitation: 'It is an estimate; stride length, pace and terrain change the real figure.',
  },
  'protein-intake': {
    whatItMeans: 'This gives a daily protein target to support your goal, whether muscle, fat loss or maintenance.',
    limitation: 'Needs rise with training and age; kidney conditions require medical guidance on protein.',
  },
  'tdee': {
    whatItMeans: 'Your TDEE is the total calories you burn in a day and the anchor for any nutrition plan.',
    limitation: 'It is an estimate; real needs shift with activity, muscle mass and metabolism.',
  },
  'calorie-deficit': {
    whatItMeans: 'This shows a calorie target below your TDEE for gradual, sustainable fat loss.',
    limitation: 'Very low intakes can backfire; a doctor-guided plan is safer with medical conditions.',
  },
  'macro': {
    whatItMeans: 'This splits your calories into protein, carbs and fats tailored to your goal.',
    limitation: 'Macro targets are starting points and should be adjusted to how your body responds.',
  },
  'water-intake': {
    whatItMeans: 'This estimates your daily fluid needs from body weight and activity level.',
    limitation: 'Needs rise with heat, exercise, illness and pregnancy; some heart or kidney conditions require limits.',
  },
};

export default CALCULATOR_GUIDANCE;
